import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, ArrowLeft, Check, ShieldCheck, Sparkles, User, Users, Lock, ChevronRight } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import NousLogo from './NousLogo';
import { Capacitor } from '@capacitor/core';

const isNative = Capacitor.isNativePlatform();

export default function Login({ onLoginSuccess }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const paramMode = searchParams.get('mode');
  const paramRole = searchParams.get('role');

  const [isRegistering, setIsRegistering] = useState(() => {
    return paramMode === 'register' || searchParams.get('register') === 'true';
  });
  const [isRecovering, setIsRecovering] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [role, setRole] = useState(() => {
    return paramRole === 'trainer' ? 'trainer' : 'athlete';
  });

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [healthDataAccepted, setHealthDataAccepted] = useState(false);
  const [ageAccepted, setAgeAccepted] = useState(false);

  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  const API_URL = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');

  // Sincronizar URL params cuando cambie externamente
  useEffect(() => {
    if (paramMode === 'register') {
      setIsRegistering(true);
    } else if (paramMode === 'login') {
      setIsRegistering(false);
    }
    if (paramRole === 'trainer') {
      setRole('trainer');
    }
  }, [paramMode, paramRole]);

  const handleTabChange = (registering) => {
    setIsRegistering(registering);
    setError(null);
    setMessage(null);
    const newParams = new URLSearchParams(searchParams);
    if (registering) {
      newParams.set('mode', 'register');
    } else {
      newParams.delete('mode');
    }
    setSearchParams(newParams, { replace: true });
  };

  const handleRoleChange = (newRole) => {
    setRole(newRole);
    const newParams = new URLSearchParams(searchParams);
    if (newRole === 'trainer') {
      newParams.set('role', 'trainer');
    } else {
      newParams.delete('role');
    }
    setSearchParams(newParams, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (isRecovering) {
      if (!email) {
        setError('Por favor, introduce tu correo electrónico.');
        return;
      }
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/auth/recover`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
        
        const text = await response.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (e) {
          throw new Error('Servidor no disponible o respuesta inválida.', { cause: e });
        }
        if (!response.ok) throw new Error(data.error || 'Error al solicitar recuperación');
        
        setMessage(data.message || 'Se ha enviado un enlace de recuperación a tu correo.');
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
      return;
    }

    if (isRegistering) {
      if (password.length < 8) {
        setError('La contraseña debe tener un mínimo de 8 caracteres.');
        return;
      }
      if (!birthDate) {
        setError('Por favor, introduce tu fecha de nacimiento.');
        return;
      }
      const birth = new Date(birthDate);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      if (isNaN(age) || age < 16) {
        setError('Debes tener al menos 16 años de edad para registrarte en NousMetrics (Art. 8 RGPD).');
        return;
      }
      if (!termsAccepted) {
        setError('Debes aceptar la Política de Privacidad y Términos de Servicio para continuar.');
        return;
      }
      if (!healthDataAccepted) {
        setError('Debes dar tu consentimiento explícito para el tratamiento de datos de salud (Art. 9 RGPD).');
        return;
      }
      if (!ageAccepted) {
        setError('Debes confirmar que tienes al menos 16 años de edad.');
        return;
      }
    }

    setLoading(true);

    try {
      const endpoint = isRegistering ? '/auth/register' : '/auth/login';
      const payload = isRegistering 
        ? {
            email,
            password,
            name,
            role,
            birthDate,
            termsAccepted,
            healthDataAccepted,
            ageAccepted,
            policyVersion: '2026-10-30'
          }
        : { email, password };

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include'
      });

      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error('El servidor no está disponible. Asegúrate de que el backend esté en ejecución.', { cause: e });
      }

      if (!response.ok) {
        throw new Error(data.error || 'Error en la autenticación');
      }

      if (isRegistering) {
        // Auto-login posterior al registro
        const loginResponse = await fetch(`${API_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
          credentials: 'include'
        });
        const loginText = await loginResponse.text();
        let loginData;
        try {
          loginData = JSON.parse(loginText);
        } catch (e) {
          throw new Error('El servidor no está disponible tras el registro.', { cause: e });
        }
        if (!loginResponse.ok) throw new Error(loginData.error || 'Error en el login posterior al registro');
        onLoginSuccess(loginData.token, loginData.user);
      } else {
        onLoginSuccess(data.token, data.user);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '0.85rem 1rem',
    borderRadius: '10px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    background: 'rgba(255, 255, 255, 0.04)',
    color: 'white',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    fontSize: '0.95rem'
  };

  return (
    <div className="login-container" style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg-color)', overflow: 'hidden' }}>
      
      {/* Lado Izquierdo: Hero de Marca y Prueba Social (Desktop) */}
      <div style={{ flex: 1.1, position: 'relative', display: 'block' }} className="hero-section login-hero">
        <img
          src="/hero_coach.png"
          alt="Atleta en entrenamiento"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to right, rgba(7, 10, 20, 0.4) 0%, rgba(7, 10, 20, 0.85) 75%, var(--bg-color) 100%)'
        }} />
        
        {/* Contenido Editorial Flotante */}
        <div style={{
          position: 'absolute',
          bottom: '8%',
          left: '8%',
          right: '8%',
          maxWidth: '560px',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          zIndex: 2
        }}>
          <div>
            <NousLogo size="large" showSlogan={true} alignment="left" variant="soma" />
          </div>

          <div>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: 'rgba(139, 92, 246, 0.2)',
              border: '1px solid rgba(139, 92, 246, 0.4)',
              color: '#c4b5fd',
              padding: '4px 12px',
              borderRadius: '20px',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
              marginBottom: '0.75rem'
            }}>
              <Sparkles size={13} color="#d946ef" /> FISIOLOGÍA CLÍNICA CON INTELIGENCIA ARTIFICIAL
            </span>
            <h1 style={{
              fontSize: '2.4rem',
              fontWeight: 800,
              color: 'white',
              lineHeight: 1.15,
              margin: '0 0 0.75rem'
            }}>
              Entrena con la certeza de los datos biomédicos.
            </h1>
            <p style={{ color: 'rgba(255, 255, 255, 0.75)', fontSize: '1.05rem', lineHeight: 1.5, margin: 0 }}>
              Desacoplamos tu capacidad biológica real de la fatiga acumulada. Umbrales VT1/VT2, variabilidad cardíaca rMSSD y carga Coggan en un panel unificado.
            </p>
          </div>

          {/* Micro-Píldoras de Confianza */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '0.75rem',
            background: 'rgba(15, 20, 35, 0.75)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '1.1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Check size={16} color="#10b981" />
              <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
                14 días de prueba Pro gratis
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Check size={16} color="#10b981" />
              <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
                Sin tarjeta de crédito
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Check size={16} color="#10b981" />
              <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
                Strava, Garmin & Apple
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ShieldCheck size={16} color="#8b5cf6" />
              <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
                RGPD Art. 9 Salud Blindado
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Lado Derecho: Formulario de Acceso y Registro */}
      <div style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: isNative ? '1.5rem 1rem' : '2rem 1.5rem',
        overflowY: 'auto'
      }}>
        <div className="glass-panel" style={{
          width: '100%',
          maxWidth: '460px',
          padding: '2.25rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.25rem',
          boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.7)',
          borderRadius: '24px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'rgba(15, 20, 35, 0.85)',
          backdropFilter: 'blur(20px)'
        }}>
          
          {/* Header con Logo y Enlace de Retorno */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <NousLogo size="medium" showSlogan={false} alignment="left" variant="soma" />
            {!isNative && (
              <button
                type="button"
                onClick={() => navigate(role === 'trainer' ? '/mentor' : '/')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'color 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.color = '#fff'}
                onMouseOut={e => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}
              >
                ← Volver a la web
              </button>
            )}
          </div>

          {isRecovering ? (
            // Formulario de Recuperación de Contraseña
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <button
                onClick={() => { setIsRecovering(false); setError(null); setMessage(null); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.6)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  alignSelf: 'flex-start',
                  padding: 0,
                  fontSize: '0.88rem'
                }}
              >
                <ArrowLeft size={16} /> Volver al acceso
              </button>

              <h2 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'white', margin: '0.25rem 0 0' }}>
                Recuperar Acceso
              </h2>
              <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem', margin: 0, lineHeight: 1.5 }}>
                Introduce tu correo y te enviaremos un enlace seguro para restablecer tu contraseña.
              </p>
              
              {message && (
                <div style={{ color: '#6ee7b7', fontSize: '0.875rem', background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '0.85rem', borderRadius: '8px' }}>
                  {message}
                </div>
              )}
              {error && (
                <div style={{ color: '#fca5a5', fontSize: '0.875rem', background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', padding: '0.85rem', borderRadius: '8px' }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem', marginTop: '0.5rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <label style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>Correo Electrónico</label>
                  <input
                    type="email"
                    placeholder="ejemplo@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={inputStyle}
                    className="input-focus"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    background: loading ? 'rgba(255,255,255,0.2)' : 'linear-gradient(135deg, var(--strava-orange) 0%, #e85d04 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '0.9rem',
                    borderRadius: '10px',
                    fontWeight: 700,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'transform 0.2s',
                    marginTop: '0.5rem'
                  }}
                  className="btn-hover"
                >
                  {loading ? 'Enviando enlace...' : 'Enviar Enlace de Recuperación'}
                </button>
              </form>
            </div>
          ) : (
            // Flujo Normal: Selector Segmentado [Iniciar Sesión | Crear Cuenta]
            <>
              {/* CONTROL SEGMENTADO TIPO SAAS PREMIUM */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '4px',
                borderRadius: '14px',
                border: '1px solid rgba(255, 255, 255, 0.08)'
              }}>
                <button
                  type="button"
                  onClick={() => handleTabChange(false)}
                  style={{
                    padding: '0.65rem 0',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    background: !isRegistering ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                    color: !isRegistering ? '#ffffff' : 'rgba(255, 255, 255, 0.6)',
                    boxShadow: !isRegistering ? '0 2px 10px rgba(0,0,0,0.3)' : 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  Iniciar Sesión
                </button>

                <button
                  type="button"
                  onClick={() => handleTabChange(true)}
                  style={{
                    padding: '0.65rem 0',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    background: isRegistering ? 'linear-gradient(135deg, var(--strava-orange) 0%, #e85d04 100%)' : 'transparent',
                    color: isRegistering ? '#ffffff' : 'rgba(255, 255, 255, 0.6)',
                    boxShadow: isRegistering ? '0 4px 15px rgba(249, 115, 22, 0.35)' : 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  Crear Cuenta Gratis
                </button>
              </div>

              {/* Título contextual */}
              <div>
                <h2 style={{ fontSize: '1.5rem', fontWeight: 800, color: 'white', margin: '0.5rem 0 0.25rem' }}>
                  {isRegistering ? 'Únete a Nous' : 'Bienvenido de nuevo'}
                </h2>
                <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.88rem', margin: 0 }}>
                  {isRegistering
                    ? '14 días de prueba Pro sin tarjeta. Cancela cuando quieras.'
                    : 'Accede a tu telemetría deportiva y prescripción diaria.'}
                </p>
              </div>

              {error && (
                <div style={{
                  color: '#fca5a5',
                  fontSize: '0.85rem',
                  background: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  padding: '0.85rem',
                  borderRadius: '10px',
                  lineHeight: 1.4
                }}>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                
                {/* SELECTOR VISUAL DE ROL (Solo en registro) */}
                {isRegistering && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', animation: 'fadeInTab 0.25s ease-out' }}>
                    <label style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                      Tipo de perfil:
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
                      {/* Opción Atleta */}
                      <button
                        type="button"
                        onClick={() => handleRoleChange('athlete')}
                        style={{
                          background: role === 'athlete' ? 'rgba(249, 115, 22, 0.12)' : 'rgba(255, 255, 255, 0.03)',
                          border: `1.5px solid ${role === 'athlete' ? 'var(--strava-orange)' : 'rgba(255, 255, 255, 0.08)'}`,
                          borderRadius: '12px',
                          padding: '0.85rem',
                          cursor: 'pointer',
                          textAlign: 'left',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                          transition: 'all 0.2s',
                          boxShadow: role === 'athlete' ? '0 0 15px rgba(249, 115, 22, 0.2)' : 'none'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <span style={{ fontSize: '1.25rem' }}>🏃‍♂️</span>
                          {role === 'athlete' && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--strava-orange)' }} />}
                        </div>
                        <div style={{ fontSize: '0.88rem', fontWeight: 800, color: 'white' }}>Soy Atleta</div>
                        <div style={{ fontSize: '0.72rem', color: 'rgba(255, 255, 255, 0.6)', lineHeight: 1.3 }}>
                          Predisposición, HRV y prescripción de series.
                        </div>
                      </button>

                      {/* Opción Entrenador */}
                      <button
                        type="button"
                        onClick={() => handleRoleChange('trainer')}
                        style={{
                          background: role === 'trainer' ? 'rgba(139, 92, 246, 0.15)' : 'rgba(255, 255, 255, 0.03)',
                          border: `1.5px solid ${role === 'trainer' ? '#8b5cf6' : 'rgba(255, 255, 255, 0.08)'}`,
                          borderRadius: '12px',
                          padding: '0.85rem',
                          cursor: 'pointer',
                          textAlign: 'left',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                          transition: 'all 0.2s',
                          boxShadow: role === 'trainer' ? '0 0 15px rgba(139, 92, 246, 0.25)' : 'none'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <span style={{ fontSize: '1.25rem' }}>⏱️</span>
                          {role === 'trainer' && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#8b5cf6' }} />}
                        </div>
                        <div style={{ fontSize: '0.88rem', fontWeight: 800, color: 'white' }}>Soy Coach</div>
                        <div style={{ fontSize: '0.72rem', color: 'rgba(255, 255, 255, 0.6)', lineHeight: 1.3 }}>
                          Roster de atletas, alertas de fatiga e informes IA.
                        </div>
                      </button>
                    </div>

                    {/* Campo Nombre (Solo en registro) */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.5rem' }}>
                      <label htmlFor="login-name" style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                        Nombre completo
                      </label>
                      <input
                        id="login-name"
                        name="name"
                        type="text"
                        autocomplete="name"
                        placeholder="Ej. Carlos Ruiz"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        style={inputStyle}
                        className="input-focus"
                      />
                    </div>

                    {/* Campo Fecha de Nacimiento (Solo en registro — A-07 / B-04 RGPD) */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.35rem' }}>
                      <label htmlFor="login-birthdate" style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                        Fecha de nacimiento (Mín. 16 años)
                      </label>
                      <input
                        id="login-birthdate"
                        name="birthDate"
                        type="date"
                        autocomplete="bday"
                        value={birthDate}
                        onChange={(e) => setBirthDate(e.target.value)}
                        max={new Date(new Date().setFullYear(new Date().getFullYear() - 16)).toISOString().split('T')[0]}
                        required
                        style={{ ...inputStyle, colorScheme: 'dark' }}
                        className="input-focus"
                      />
                    </div>
                  </div>
                )}

                {/* Campo Email */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <label htmlFor="login-email" style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                    Correo Electrónico
                  </label>
                  <input
                    id="login-email"
                    name="email"
                    type="email"
                    autocomplete="email"
                    autocorrect="off"
                    autocapitalize="off"
                    spellcheck="false"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={inputStyle}
                    className="input-focus"
                  />
                </div>
                
                {/* Campo Contraseña */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label htmlFor="login-password" style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                      Contraseña
                    </label>
                    {!isRegistering && (
                      <button
                        type="button"
                        onClick={() => { setIsRecovering(true); setError(null); }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#38bdf8',
                          fontSize: '0.8rem',
                          cursor: 'pointer',
                          padding: 0
                        }}
                      >
                        ¿Olvidaste tu contraseña?
                      </button>
                    )}
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="login-password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autocomplete={isRegistering ? "new-password" : "current-password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      style={{ ...inputStyle, paddingRight: '2.5rem' }}
                      className="input-focus"
                    />
                    <button
                      type="button"
                      aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                      aria-pressed={showPassword}
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute',
                        right: '0.8rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: 'rgba(255,255,255,0.5)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                    </button>
                  </div>

                  {/* Indicador de Fortaleza de Contraseña en Registro */}
                  {isRegistering && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '0.74rem',
                      color: password.length >= 8 ? '#10b981' : 'rgba(255, 255, 255, 0.45)',
                      marginTop: '0.1rem',
                      transition: 'color 0.2s'
                    }}>
                      <Check size={13} color={password.length >= 8 ? '#10b981' : 'rgba(255, 255, 255, 0.3)'} />
                      <span>Mínimo 8 caracteres {password.length > 0 && `(${password.length}/8)`}</span>
                    </div>
                  )}
                </div>

                {/* Bloque Legal Compacto y Confiable (Solo Registro) */}
                {isRegistering && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.65rem',
                    padding: '0.85rem',
                    background: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    borderRadius: '12px'
                  }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        style={{ marginTop: '0.15rem', accentColor: 'var(--strava-orange)' }}
                      />
                      <span style={{ lineHeight: 1.35 }}>
                        Acepto los <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--strava-orange)', textDecoration: 'none', fontWeight: 600 }}>Términos de Servicio</a> y la <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--strava-orange)', textDecoration: 'none', fontWeight: 600 }}>Política de Privacidad</a>.
                      </span>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={healthDataAccepted}
                        onChange={(e) => setHealthDataAccepted(e.target.checked)}
                        style={{ marginTop: '0.15rem', accentColor: 'var(--strava-orange)' }}
                      />
                      <span style={{ lineHeight: 1.35 }}>
                        Consiento el procesamiento de mis datos de salud y biometría deportiva (<strong style={{ color: '#fff' }}>Art. 9 RGPD</strong>).
                      </span>
                    </label>

                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={ageAccepted}
                        onChange={(e) => setAgeAccepted(e.target.checked)}
                        style={{ marginTop: '0.15rem', accentColor: 'var(--strava-orange)' }}
                      />
                      <span style={{ lineHeight: 1.35 }}>
                        Confirmo que tengo al menos 16 años de edad.
                      </span>
                    </label>
                  </div>
                )}

                {/* BOTÓN CTA PRINCIPAL */}
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    background: loading
                      ? 'rgba(255,255,255,0.2)'
                      : role === 'trainer' && isRegistering
                        ? 'linear-gradient(135deg, #8b5cf6 0%, #d946ef 100%)'
                        : 'linear-gradient(135deg, var(--strava-orange) 0%, #e85d04 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '0.95rem',
                    borderRadius: '12px',
                    fontWeight: 800,
                    fontSize: '1rem',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    marginTop: '0.25rem',
                    transition: 'transform 0.2s, filter 0.2s, box-shadow 0.2s',
                    boxShadow: role === 'trainer' && isRegistering
                      ? '0 6px 20px rgba(139, 92, 246, 0.4)'
                      : '0 6px 20px rgba(249, 115, 22, 0.35)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  className="btn-hover"
                >
                  {loading ? (
                    'Procesando...'
                  ) : isRegistering ? (
                    role === 'trainer' ? (
                      <>
                        <span>Iniciar Piloto de Entrenador</span>
                        <ChevronRight size={18} />
                      </>
                    ) : (
                      <>
                        <span>Crear Cuenta Gratis (14 Días)</span>
                        <ChevronRight size={18} />
                      </>
                    )
                  ) : (
                    <>
                      <span>Iniciar Sesión</span>
                      <ChevronRight size={18} />
                    </>
                  )}
                </button>

                {/* Micro-copy de Seguridad y Privacidad */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  fontSize: '0.74rem',
                  color: 'rgba(255, 255, 255, 0.45)',
                  marginTop: '0.25rem'
                }}>
                  <Lock size={12} />
                  <span>Conexión segura SSL de 256 bits · Sin permanencias</span>
                </div>

              </form>
            </>
          )}

        </div>
      </div>
      
      <style>{`
        @media (min-width: 900px) {
          .hero-section {
            display: block !important;
          }
        }
        .input-focus:focus {
          border-color: var(--strava-orange) !important;
          box-shadow: 0 0 0 2px rgba(249, 115, 22, 0.2) !important;
        }
        .btn-hover:hover:not(:disabled) {
          transform: translateY(-2px);
          filter: brightness(1.08);
        }
      `}</style>
    </div>
  );
}
